<template>
   <div class="w-full mb-5">
      <label for="name" :class="!errors.name ? 'text-gray-700' : 'text-theme-error'" class="block text-sm font-medium">Name</label>
      <input v-model="subAgent.name" type="text" :class="!errors.name ? 'border-gray-300' : 'border-theme-error'" class="mt-1 outline-none border rounded-sm h-10 w-full block pl-2 text-gray-500 text-sm" placeholder="Enter name">
   </div>
   <div class="w-full mb-5">
      <label for="email" :class="!errors.email ? 'text-gray-700' : 'text-theme-error'" class="block text-sm font-medium">
         Email{{ subAgent.website_login ? ' *' : ' (Optional)' }}
      </label>
      <input v-model="subAgent.email" type="text" :class="!errors.email ? 'border-gray-300' : 'border-theme-error'" class="mt-1 outline-none border rounded-sm h-10 w-full block pl-2 text-gray-500 text-sm" placeholder="Enter email address">
   </div>
   <div class="w-full mb-5">
      <label for="phone" :class="!errors.phone ? 'text-gray-700' : 'text-theme-error'" class="block text-sm font-medium">Phone Number</label>
      <input v-model="subAgent.phone" type="text" :class="!errors.phone ? 'border-gray-300' : 'border-theme-error'" class="mt-1 outline-none border rounded-sm h-10 w-full block pl-2 text-gray-500 text-sm" placeholder="Enter phone number">
   </div>

   <!-- Active Status toggle -->
   <div class="w-full mb-5 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div>
         <span class="block text-sm font-semibold text-gray-800">Status</span>
         <span class="text-xs text-gray-500">{{ subAgent.active ? 'Active' : 'Inactive' }}</span>
      </div>
      <button
         type="button"
         @click="subAgent.active = !subAgent.active"
         :class="subAgent.active ? 'bg-indigo-600' : 'bg-gray-200'"
         class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
      >
         <span
            :class="subAgent.active ? 'translate-x-5' : 'translate-x-0'"
            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
         />
      </button>
   </div>

   <!-- Is Admin Role toggle -->
   <div class="w-full mb-5 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div>
         <span class="block text-sm font-semibold text-gray-800">Is Admin</span>
         <span class="text-xs text-gray-500">{{ subAgent.is_admin ? 'Admin' : 'Staff' }}</span>
      </div>
      <button
         type="button"
         @click="subAgent.is_admin = !subAgent.is_admin"
         :class="subAgent.is_admin ? 'bg-indigo-600' : 'bg-gray-200'"
         class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
      >
         <span
            :class="subAgent.is_admin ? 'translate-x-5' : 'translate-x-0'"
            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
         />
      </button>
   </div>

   <!-- Allow Website Login toggle -->
   <div class="w-full mb-5 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div>
         <span class="block text-sm font-semibold text-gray-800">Allow Website Login</span>
         <span class="text-xs text-gray-500">{{ subAgent.website_login ? 'Allowed' : 'Disabled' }}</span>
      </div>
      <button
         type="button"
         @click="toggleWebsiteLogin"
         :class="subAgent.website_login ? 'bg-indigo-600' : 'bg-gray-200'"
         class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
      >
         <span
            :class="subAgent.website_login ? 'translate-x-5' : 'translate-x-0'"
            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
         />
      </button>
   </div>

   <!-- Website Password field -->
   <div v-if="subAgent.website_login" class="w-full mb-5">
      <label for="password" :class="!errors.password ? 'text-gray-700' : 'text-theme-error'" class="block text-sm font-medium">
         {{ props.data ? 'New Password (leave empty to keep current)' : 'Website Password *' }}
      </label>
      <div class="relative mt-1">
         <input
            v-model="subAgent.password"
            :type="isPasswordVisible ? 'text' : 'password'"
            :class="!errors.password ? 'border-gray-300' : 'border-theme-error'"
            class="outline-none border rounded-sm h-10 w-full block pl-2 pr-10 text-gray-500 text-sm"
            placeholder="Enter password"
         >
         <button
            type="button"
            @click="isPasswordVisible = !isPasswordVisible"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
         >
            <i :class="isPasswordVisible ? 'pi pi-eye' : 'pi pi-eye-slash'" class="text-sm"></i>
         </button>
      </div>
   </div>

   <div class="flex">
      <button @click="handleSubmit()" type="button" :class="isLoading ? 'cursor-not-allowed opacity-70' : 'hover:bg-indigo-700'" class="ml-auto mt-10 bg-indigo-600 py-2 px-10 border border-transparent rounded-md shadow-sm text-sm font-medium text-white outline-none">
         Save
      </button>
   </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Agent from '@/composables/admin/agent'
import { isValidEmail, resetErrors, handleApiError, isLoggedIn } from '@/utils/common'
import { Agent as AgentType } from '@/types/admin'

const props = defineProps(['data'])
const emit = defineEmits(['success', 'error'])
const isLoading = ref<boolean>(false)
const isPasswordVisible = ref<boolean>(false)
const { createSubAgent, updateSubAgent } = Agent()
const authUser = isLoggedIn()

const subAgent = ref<AgentType>({
   id: '',
   name: '',
   email: '',
   commission: 0,
   address: '',
   phone: '',
   isParent: false,
   website_login: false,
   is_admin: false,
   active: true,
   password: ''
})

const errors = ref<any>({
   name: false,
   email: false,
   phone: false,
   password: false
})

if (props.data) {
   subAgent.value = {
      id: props.data.id || '',
      name: props.data.name || '',
      email: props.data.email || '',
      commission: props.data.commission || 0,
      address: props.data.address || '',
      phone: props.data.phone || '',
      isParent: props.data.isParent || false,
      website_login: props.data.website_login === true || props.data.website_login === 1,
      is_admin: props.data.is_admin === true || props.data.is_admin === 1,
      active: props.data.status === 1 || props.data.status === true,
      password: ''
   }
}

const toggleWebsiteLogin = () => {
   subAgent.value.website_login = !subAgent.value.website_login
}

const validateForm = () : boolean => {

   let response = true

   if (subAgent.value.name == '') {
      response = false
      errors.value.name = true
   }

   if (subAgent.value.website_login || subAgent.value.email != '') {
      if (subAgent.value.email == '' || !isValidEmail(subAgent.value.email)) {
         response = false
         errors.value.email = true
      }
   }

   if (subAgent.value.phone == '') {
      response = false
      errors.value.phone = true
   }

   if (subAgent.value.website_login) {
      if (!props.data) {
         if (!subAgent.value.password || subAgent.value.password.length < 6) {
            response = false
            errors.value.password = true
         }
      } else {
         if (subAgent.value.password && subAgent.value.password.length < 6) {
            response = false
            errors.value.password = true
         }
      }
   }

   return response 
}

const handleSubmit = async () => {

   resetErrors(errors.value)
   const isValid = validateForm()

   if (isValid && !isLoading.value) {
      
      isLoading.value = true

      if (!props.data) {

         const response = await createSubAgent(subAgent.value)
         if (response.status == 201) {
            emit('success')
         } else {
            const error = handleApiError(response)
            emit('error', error)
         }
      } else {

         const response = await updateSubAgent(subAgent.value)
         if (response.status == 200) {
            emit('success')
         } else {
            const error = handleApiError(response)
            emit('error', error)
         }
      }

      isLoading.value = false
   }
}

</script>